import axios from 'axios';
import { jmapGatewayClient, JMAP_GATEWAY_URL } from './apiClient';
import type { UserPreferences, MfaStatus, MfaSetup, BrandingSettings } from '@/types/api';

export async function updateDisplayName(token: string, displayName: string) {
  return await jmapGatewayClient.patch<{ displayName: string }>(
    '/user/profile',
    token,
    { displayName }
  );
}

export async function getPreferences(token: string) {
  return await jmapGatewayClient.get<UserPreferences>(
    '/user/preferences',
    token
  );
}

export async function updatePreferences(token: string, payload: UserPreferences) {
  return await jmapGatewayClient.patch<UserPreferences>(
    '/user/preferences',
    token,
    payload
  );
}

export async function getMfaStatus(token: string) {
  return await jmapGatewayClient.get<MfaStatus>(
    '/user/mfa/status',
    token
  );
}

export async function startMfaSetup(token: string) {
  return await jmapGatewayClient.post<MfaSetup>(
    '/user/mfa/setup',
    token,
    {}
  );
}

export async function verifyMfa(token: string, code: string) {
  return await jmapGatewayClient.post<{ status: string }>(
    '/user/mfa/verify',
    token,
    { code }
  );
}

export async function disableMfa(token: string) {
  return await jmapGatewayClient.post<void>(
    '/user/mfa/disable',
    token,
    {}
  );
}

export async function getBranding(token: string) {
  console.log('[tenantClient] Fetching branding from /user/branding');
  try {
    const branding = await jmapGatewayClient.get<BrandingSettings>('/user/branding', token);
    console.log('[tenantClient] Branding fetch successful:', branding);
    return branding;
  } catch (error: any) {
    console.error('[tenantClient] Branding fetch failed:', error.response?.status, error.response?.data || error.message);
    throw error;
  }
}

export async function uploadProfileAvatar(token: string, file: File) {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${JMAP_GATEWAY_URL}/user/avatar`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
}

export async function fetchProfileAvatar(token: string): Promise<Blob | null> {
  try {
    const response = await axios.get(`${JMAP_GATEWAY_URL}/user/avatar`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'blob',
    });
    return response.data;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

// In-flight dedupe + short-lived negative cache for avatar 404s.
//
// EmailList, ThreadViewer, and EmailViewer each have their own
// "fetch missing avatars" effect, and on initial render they all see
// the zustand cache as empty for the same senders → all three fire the
// same /user/avatar request in parallel. The 404 logs in the console
// 3× per sender. We dedupe by sharing the in-flight Promise across
// concurrent callers, and we remember 404s for a short window so the
// browser doesn't re-hit the endpoint when the cache gets evicted or
// when a different surface renders the same sender.
const avatarInFlight = new Map<string, Promise<Blob | null>>();
const avatarNegativeCache = new Map<string, number>(); // email -> expiry ms
const AVATAR_NEG_TTL_MS = 5 * 60_000;

export async function fetchUserAvatarByEmail(token: string, email: string): Promise<Blob | null> {
  const key = email.toLowerCase();
  const negUntil = avatarNegativeCache.get(key);
  if (negUntil && negUntil > Date.now()) return null;
  const inflight = avatarInFlight.get(key);
  if (inflight) return inflight;
  const p = (async () => {
    try {
      const response = await axios.get(`${JMAP_GATEWAY_URL}/user/avatar`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { email },
        responseType: 'blob',
      });
      return response.data as Blob;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        avatarNegativeCache.set(key, Date.now() + AVATAR_NEG_TTL_MS);
        return null;
      }
      throw error;
    } finally {
      avatarInFlight.delete(key);
    }
  })();
  avatarInFlight.set(key, p);
  return p;
}
